import { createSelector } from '@reduxjs/toolkit';
import { pick } from 'lodash';

import { FormStepId } from 'shared/config/formStepId';
import { sendSentryClientErrorOnce } from 'shared/lib';
import type { SliceStateFromReducer } from 'shared/types';

import type { TCreditFormCommonFields } from 'entities/creditForm';

import type { creditFormSlice } from './creditForm.slice';

export type TCreditFormState = SliceStateFromReducer<typeof creditFormSlice>;
export type TCreditFormDictionaryName = keyof TCreditFormState['creditForm']['dictionaries'];

export const selectCreditPurposeInfo = (state: TCreditFormState) => state.creditForm.creditPurposeForm;
export const selectContactsInfo = (state: TCreditFormState) => state.creditForm.contactsForm;
export const selectPassportInfo = (state: TCreditFormState) => state.creditForm.passportForm;
export const selectWorkplaceInfo = (state: TCreditFormState) => state.creditForm.workplaceForm;
export const selectAdditionalInfo = (state: TCreditFormState) => state.creditForm.additionalDataForm;
export const selectIsFilledByEsia = (state: TCreditFormState) => state.creditForm.isFilledByEsiaStatus;

export const selectCreditPurposeInfoData = (state: TCreditFormState) => {
  if (!state.creditForm.creditPurposeForm.data || !Object.keys(state.creditForm.creditPurposeForm.data).length) {
    return state.creditForm.creditPurposeForm.defaults;
  }

  return state.creditForm.creditPurposeForm.data;
};

export const selectCreditParams = createSelector([selectCreditPurposeInfoData], (creditPurposeData) => {
  const { amount, period, purpose } = creditPurposeData;
  return {
    amount,
    period,
    purpose,
  };
});

export const selectContactsInfoData = (state: TCreditFormState) => {
  if (!state.creditForm.contactsForm.data || !Object.keys(state.creditForm.contactsForm.data).length) {
    return state.creditForm.contactsForm.defaults;
  }

  return state.creditForm.contactsForm.data;
};

export const selectLandingInfoData = (state: TCreditFormState) => {
  const { creditPurposeForm, contactsForm, passportForm } = state.creditForm;
  return {
    ...(contactsForm.data ?? contactsForm.defaults),
    ...(creditPurposeForm.data ?? creditPurposeForm.defaults),
    ...pick(passportForm.data ?? passportForm.defaults, ['fullName', 'gender', 'region', 'regionLocationRoute']),
  };
};

export const selectLandingInfoAuthData = createSelector([selectLandingInfoData], (landingData) => {
  const { mobilePhone, ...rest } = landingData;
  return {
    ...rest,
    phone: mobilePhone,
  };
});

export const selectContactsInfoAuthData = createSelector([selectContactsInfoData], (contactsData) => {
  const { mobilePhone, email } = contactsData;
  return {
    phone: mobilePhone,
    email,
  };
});

export const selectPassportInfoData = (state: TCreditFormState) => {
  if (!state.creditForm.passportForm.data || !Object.keys(state.creditForm.passportForm.data).length) {
    return state.creditForm.passportForm.defaults;
  }

  return state.creditForm.passportForm.data;
};

export const selectWorkplaceInfoData = (state: TCreditFormState) => {
  if (!state.creditForm.workplaceForm.data || !Object.keys(state.creditForm.workplaceForm.data).length) {
    return state.creditForm.workplaceForm.defaults;
  }

  return state.creditForm.workplaceForm.data;
};

export const selectAdditionalInfoData = (state: TCreditFormState) => {
  if (!state.creditForm.additionalDataForm.data || !Object.keys(state.creditForm.additionalDataForm.data).length) {
    return state.creditForm.additionalDataForm.defaults;
  }

  return state.creditForm.additionalDataForm.data;
};

export const selectCreditFormDictionaries =
  <T extends TCreditFormDictionaryName[]>(dictionaryNames: T) =>
  (state: TCreditFormState) => {
    return pick(state.creditForm.dictionaries, dictionaryNames);
  };

export const selectAllFormFields = createSelector(
  [
    selectAdditionalInfoData,
    selectContactsInfoData,
    selectCreditPurposeInfoData,
    selectPassportInfoData,
    selectWorkplaceInfoData,
  ],
  (additionalData, contactsData, creditPurposeData, passportData, workplaceData): TCreditFormCommonFields => {
    return {
      ...additionalData,
      ...contactsData,
      ...creditPurposeData,
      ...passportData,
      ...workplaceData,
    };
  },
);

export const selectStepsFields = (stepNames: Array<(typeof FormStepId)[keyof typeof FormStepId]>) =>
  createSelector(
    [
      selectAdditionalInfoData,
      selectContactsInfoData,
      selectCreditPurposeInfoData,
      selectPassportInfoData,
      selectWorkplaceInfoData,
    ],
    (
      additionalData,
      contactsData,
      creditPurposeData,
      passportData,
      workplaceData,
      // eslint-disable-next-line max-params
    ): Partial<TCreditFormCommonFields> => {
      const formData = {
        [FormStepId.CreditPurposeInfo]: creditPurposeData,
        [FormStepId.ContactsInfo]: contactsData,
        [FormStepId.PassportInfo]: passportData,
        [FormStepId.WorkplaceInfo]: workplaceData,
        [FormStepId.AdditionalInfo]: additionalData,
      };
      return stepNames.reduce<Partial<TCreditFormCommonFields>>((prev, stepName) => {
        return {
          ...prev,
          ...formData[stepName],
        };
      }, {});
    },
  );

export const selectRequiredFieldsForUpdate = createSelector(
  [selectPassportInfoData],
  (passportData): Partial<TCreditFormCommonFields> => {
    return {
      fullName: passportData.fullName,
      birthDate: passportData.birthDate,
      passportNumber: passportData.passportNumber,
      passportIssueDate: passportData.passportIssueDate,
      passportIssuedBy: passportData.passportIssuedBy,
      unitCode: passportData.unitCode,
    };
  },
);

export const defaultStepperConfigSelector = createSelector(
  selectCreditPurposeInfo,
  selectContactsInfo,
  selectPassportInfo,
  selectWorkplaceInfo,
  selectAdditionalInfo,
  // eslint-disable-next-line max-params
  (creditPurposeInfoForm, contactsInfoForm, passportInfoForm, workplaceInfoForm, additionalInfoForm) => ({
    [FormStepId.CreditPurposeInfo]: creditPurposeInfoForm,
    [FormStepId.ContactsInfo]: contactsInfoForm,
    [FormStepId.PassportInfo]: passportInfoForm,
    [FormStepId.WorkplaceInfo]: workplaceInfoForm,
    [FormStepId.AdditionalInfo]: additionalInfoForm,
  }),
);

export const checkIsFormFullFilledSelector = createSelector(
  defaultStepperConfigSelector,
  (steps) =>
    !Object.values(steps).some((value) => {
      return !value.isFullFilled;
    }),
);

export const areSelectedStepsFullFilled = (stepNames: Array<ValuesOf<typeof FormStepId>>) =>
  createSelector(defaultStepperConfigSelector, (steps) => {
    return stepNames.every((stepId) => steps[stepId]?.isFullFilled);
  });

export const formReadyForSendingSelector = createSelector(
  checkIsFormFullFilledSelector,
  (isFullFilled) => isFullFilled,
);

export const isFormFullFilledThunk =
  ({
    successCallback,
    failureCallback,
  }: {
    successCallback?: () => void;
    failureCallback: () => void;
  }): ThunkResult<void> =>
  (_, getState) => {
    const isFullFilled = checkIsFormFullFilledSelector(getState());
    if (!isFullFilled) {
      sendSentryClientErrorOnce(true, '[AnketaWidget] данные вернулись неполные', {
        level: 'log',
      });
      failureCallback();
      return;
    }

    successCallback?.();
  };

export const runAfterCheckFormReadyThunk =
  (callback: (isReady: boolean) => void): ThunkResult<void> =>
  (_, getState) => {
    const isReady = formReadyForSendingSelector(getState());

    callback(isReady);
  };

export const selectFullName = (state: TCreditFormState) => {
  return state.creditForm.passportForm.data?.fullName;
};

export const selectRegistrationAddressFields = (state: TCreditFormState) => {
  const { registrationAddress, registrationAddressFlat } = state.creditForm.passportForm.data || {};
  return { registrationAddress, registrationAddressFlat };
};

export const selectMobilePhone = (state: TCreditFormState) => {
  const { mobilePhone } = state.creditForm.contactsForm.data || {};
  return mobilePhone;
};

export const selectAdditionalPhone = (state: TCreditFormState) => {
  const { additionalPhone } = state.creditForm.additionalDataForm.data || {};
  return additionalPhone;
};

export const selectWorkPhone = (state: TCreditFormState) => {
  const { workPhone } = state.creditForm.workplaceForm.data || {};
  return workPhone;
};

export const selectEmployerType = (state: TCreditFormState) => {
  const { employerType } = state.creditForm.workplaceForm.data || {};
  return employerType;
};

export const selectIsStepsOpenedInPopup = (state: TCreditFormState) => {
  const { creditPurposeForm, workplaceForm, contactsForm, additionalDataForm, passportForm } = state.creditForm;
  return {
    [FormStepId.CreditPurposeInfo]: creditPurposeForm.isOpenedInPopup,
    [FormStepId.ContactsInfo]: contactsForm.isOpenedInPopup,
    [FormStepId.PassportInfo]: passportForm.isOpenedInPopup,
    [FormStepId.WorkplaceInfo]: workplaceForm.isOpenedInPopup,
    [FormStepId.AdditionalInfo]: additionalDataForm.isOpenedInPopup,
  };
};

export const isAnyStepOpenedInPopup = createSelector(selectIsStepsOpenedInPopup, (steps) => {
  return Object.values(steps).some((isOpened) => isOpened);
});

export const isCurrentStepOpenedInPopup = (stepId: ValuesOf<typeof FormStepId>) =>
  createSelector(selectIsStepsOpenedInPopup, (steps) => {
    return steps[stepId];
  });

export const selectRegionLocationData = (state: TCreditFormState) => {
  return state.creditForm.passportForm.data?.regionLocation;
};

export const selectGender = (state: TCreditFormState) => {
  const { gender } = state.creditForm.passportForm.data || {};

  return gender;
};
